import * as React from 'react';
import {Mutation} from 'react-apollo';

import {IntegrationWidget} from 'shells/integration-widget/integration-widget';
import Office365LogoSvg from 'shells/logos/office-365.svg';

import type {
    setOffice365MailSendIsEnabled as setOffice365MailSendIsEnabledMutation,
    setOffice365MailSendIsEnabledVariables as setOffice365MailSendIsEnabledMutationVariables,
} from 'nutshell-graphql-types';

import {authToService} from '../../integrations/auth-to-service';
import {ClearIntegration} from '../../integrations/clear-integration';
import {getAuthUrl} from '../../integrations/integration-mutation-response-helpers';

import {QUERIES_TO_REFETCH_AFTER_EMAIL_UPDATE} from '../constants';
import SET_OFFICE365_MAIL_SEND_IS_ENABLED from '../graphql/mutations/set-office365-mail-send-is-enabled.graphql';

import {updateCacheFromMutation} from './update-cache-from-mutation';

const office365Logo = <Office365LogoSvg width={24} />;
const windowFeatures = {
    toolbar: 'no',
    location: 'no',
    width: 600,
    height: 850,
} as const;

class Office365EnableMutation extends Mutation<
    setOffice365MailSendIsEnabledMutation,
    setOffice365MailSendIsEnabledMutationVariables
> {}

type Props = {
    username: string;
    isEnabled: boolean;
    onAuthStart: () => void;
    onAuthSuccess: () => Promise<any>;
    onAuthError: (arg1: string[]) => void;
    errors?: string[];
};

export function EmailWidgetOffice365(props: Props) {
    const [isAuthenticating, setIsAuthenticating] = React.useState<boolean>(false);

    return (
        <ClearIntegration>
            {({clearIntegration}) => (
                <Office365EnableMutation
                    mutation={SET_OFFICE365_MAIL_SEND_IS_ENABLED}
                    update={updateCacheFromMutation}
                    // @ts-expect-error
                    refetchQueries={QUERIES_TO_REFETCH_AFTER_EMAIL_UPDATE}
                >
                    {(enableOffice365, {loading: isMutationInFlight}) => {
                        const hasErrors = props.errors && props.errors.length > 0;
                        let status = props.isEnabled && !hasErrors ? 'enabled' : 'disabled';
                        if (isMutationInFlight || isAuthenticating) {
                            status = props.isEnabled ? 'disabling' : 'enabling';
                        }
                        const statusMessage = hasErrors ? 'Needs attention' : undefined;

                        return (
                            <IntegrationWidget
                                // @ts-expect-error
                                status={status}
                                statusMessage={statusMessage}
                                account={props.username}
                                errors={props.errors}
                                logo={office365Logo}
                                onConnectClick={() => {
                                    enableOffice365({variables: {isEnabled: true}})
                                        .then(getAuthUrl)
                                        .then((authUrl) => {
                                            // If we get an authUrl back, then we need to auth
                                            // Highly unlikely in the case of google,
                                            // because we get all scopes up front.
                                            if (authUrl) {
                                                props.onAuthStart();
                                                setIsAuthenticating(true);
                                                authToService({
                                                    authUrl,
                                                    windowFeatures,
                                                    authCheckUrl: '/office365/has-auth',
                                                })
                                                    .then(({hasAuth}) => {
                                                        if (hasAuth) {
                                                            props.onAuthSuccess().then(() => {
                                                                setIsAuthenticating(false);
                                                            });
                                                        } else {
                                                            setIsAuthenticating(false);
                                                        }
                                                    })
                                                    .catch((errMsg) => {
                                                        props.onAuthError(errMsg);
                                                        setIsAuthenticating(false);
                                                    });
                                            }
                                        });
                                }}
                                onDisconnectClick={() => {
                                    enableOffice365({variables: {isEnabled: false}});
                                }}
                                revokeWarning='This will also remove this account from calendar and task sync.'
                                onRevokeClick={() => {
                                    props.onAuthStart();
                                    clearIntegration('OFFICE365')
                                        .then(() => {
                                            props.onAuthSuccess();
                                        })
                                        // TODO: does this actually work?
                                        .catch((errMsg) => {
                                            props.onAuthError(errMsg);
                                        });
                                }}
                            />
                        );
                    }}
                </Office365EnableMutation>
            )}
        </ClearIntegration>
    );
}
